import { useAtom, useSetAtom } from 'jotai';
import React from 'react';
import {
  $privatePaths,
  fetchPrivatePaths,
  normalizePrivatePath,
} from './private-paths';

const EMPTY_PRIVATE_PATHS: readonly string[] = [];

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * Workspace-relative paths marked private for `workspaceId`, or an empty list
 * for a local workspace / while the first read is still in flight.
 *
 * The set is derived state only: it is read once per workspace and then kept
 * current by `useMarkPathPrivate` whenever privacy is toggled.
 */
export function usePrivatePaths(workspaceId: string | null): readonly string[] {
  const [state, setState] = useAtom($privatePaths);

  React.useEffect(() => {
    if (!workspaceId) {
      setState({ paths: EMPTY_PRIVATE_PATHS, workspaceId: null });

      return;
    }

    const controller = new AbortController();
    // Drop the previous workspace's markers before reading the new ones; a
    // remount of the same workspace keeps its markers until the read lands.
    setState((previous) =>
      previous.workspaceId === workspaceId
        ? previous
        : { paths: EMPTY_PRIVATE_PATHS, workspaceId },
    );

    void (async () => {
      try {
        const paths = await fetchPrivatePaths(workspaceId, controller.signal);
        if (controller.signal.aborted) {
          return;
        }
        setState({ paths, workspaceId });
      } catch (error) {
        if (controller.signal.aborted || isAbortError(error)) {
          return;
        }
        // Lock badges are derived UI state: a failed read leaves the set
        // empty instead of surfacing an error over the sidebar.
      }
    })();

    return () => controller.abort();
  }, [setState, workspaceId]);

  return state.workspaceId === workspaceId ? state.paths : EMPTY_PRIVATE_PATHS;
}

/**
 * Records a successful privacy toggle in the marker set, so the lock badge
 * appears or disappears without another round trip.
 */
export function useMarkPathPrivate(): (
  workspaceId: string,
  path: string,
  isPrivate: boolean,
) => void {
  const setState = useSetAtom($privatePaths);

  return React.useCallback(
    (workspaceId: string, path: string, isPrivate: boolean) => {
      const normalizedPath = normalizePrivatePath(path);

      setState((previous) => {
        // Markers for another (or not yet loaded) workspace stay untouched.
        if (previous.workspaceId !== workspaceId) {
          return previous;
        }

        const paths = isPrivate
          ? [...new Set([...previous.paths, normalizedPath])]
          : previous.paths.filter((candidate) => candidate !== normalizedPath);

        return { paths, workspaceId };
      });
    },
    [setState],
  );
}
